import React, { useState, createContext, useCallback, useContext } from 'react';
import type { PersonFragment } from '@graphql/fragments/person.graphql.generated';
import type { SelectedPeople, SelectedPeopleDispatch } from '@shared/context/people.types';
import { getSessionStorage } from '@shared/core';
import { addError } from '@shared/utils/logger';

export const PeopleContext = createContext<{
  selectedPeople: SelectedPeople;
  setSelectedPeople: SelectedPeopleDispatch;
  clearSelectedPeople: () => void;
}>({
  selectedPeople: [],
  setSelectedPeople() {},
  clearSelectedPeople() {},
});

const buildSessionKey = (storageKey: string) => `people:${storageKey}`;

const readPersistedPeople = (storageKey: string | undefined): ReadonlyArray<Partial<PersonFragment>> => {
  if (!storageKey) return [];
  try {
    const raw = getSessionStorage().getItem(buildSessionKey(storageKey));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    addError(error, { context: 'PeopleContext#readPersistedPeople' });
    return [];
  }
};

const persistPeople = (storageKey: string | undefined, people: ReadonlyArray<Partial<PersonFragment>>) => {
  if (!storageKey) return;
  try {
    getSessionStorage().setItem(buildSessionKey(storageKey), JSON.stringify(people));
  } catch (error) {
    addError(error, { context: 'PeopleContext#persistPeople' });
  }
};

type PeopleProviderProps = Readonly<{
  children: React.ReactNode;
  /** When provided, the selection is persisted to sessionStorage under this key so it survives page refreshes. */
  storageKey?: string;
}>;

export const PeopleProvider: React.FC<PeopleProviderProps> = ({ children, storageKey }) => {
  const [selectedPeople, setSelectedPeopleRaw] = useState<ReadonlyArray<Partial<PersonFragment>>>(() => readPersistedPeople(storageKey));

  const setSelectedPeople: SelectedPeopleDispatch = useCallback(
    valueOrUpdater => {
      setSelectedPeopleRaw(prev => {
        const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
        persistPeople(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  const clearSelectedPeople = useCallback(() => {
    setSelectedPeopleRaw([]);
    if (storageKey) {
      try {
        getSessionStorage().removeItem(buildSessionKey(storageKey));
      } catch {
        // best-effort cleanup
      }
    }
  }, [storageKey]);

  return (
    <PeopleContext.Provider
      value={{
        selectedPeople,
        setSelectedPeople,
        clearSelectedPeople,
      }}
    >
      {children}
    </PeopleContext.Provider>
  );
};

export const usePeopleContext = () => {
  return useContext(PeopleContext);
};
